import { useEffect, useState, useCallback, useMemo, Fragment } from 'react'
import type {
  DrawLottery, DrawLotteryWithCurrent, DrawWithResult, DrawStatus, DrawScheduleConfig,
} from '@nexbit/types'
import { normalizeRounds } from '@nexbit/types'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import {
  apiDrawGetLotteries,
  apiDrawGetDraws,
  apiDrawCreate,
  apiDrawNextSlot,
  apiDrawUpdateSettings,
  apiDrawSetMarket,
  apiDrawSetActive,
  apiDrawClose,
  apiDrawReopen,
  apiDrawEnterResult,
  apiDrawUpdateResult,
  apiDrawSettle,
  apiDrawVoid,
  apiDrawCancel,
  apiDrawSkip,
  apiDrawRules,
  apiDrawSaveRules,
  ApiRequestError,
  type DrawSettleSummary,
  type DrawVoidSummary,
  type DrawCancelSummary,
  type DrawSlot,
} from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronUp,
  CalendarClock,
  CalendarPlus,
  Coins,
  Info,
  Loader2,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Trash2,
  Ban,
  CalendarOff,
  BookText,
  Settings2,
  Ticket,
  Undo2,
  X,
} from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import { useCan } from '@/hooks/usePermission'
import { useAuth } from '@/contexts/auth'
import { AddLotteryDialog } from './AddLotteryDialog'
import { RulesEditor, RulesPreview } from '@/components/RulesEditor'
import type { Dict } from '@/lib/i18n'
import {
  roundDraftsFrom, validateRounds, toRoundConfigs, describeRounds, MAX_ROUNDS,
  openBeforeModeOf, clockFromSecondsBefore, secondsBeforeFromClock, isValidClock, newRoundDraft,
  formatDurationTh, durationToSeconds, secondsToDuration, type OpenBeforeMode,
  type RoundDraft,
} from '@/lib/schedule'

const BOTTOM_TWO_RE = /^\d{2}$/
const THREE_DIGITS_RE = /^\d{3}$/

type FormMode = 'enter' | 'edit'

function statusMeta(
  t: Dict,
  status: DrawStatus,
): { label: string; variant: 'success' | 'warning' | 'secondary' | 'outline' | 'default' } {
  switch (status) {
    case 'open':
      return { label: t.draws.statusOpen, variant: 'success' }
    case 'closed':
      return { label: t.draws.statusClosed, variant: 'warning' }
    case 'resulted':
      return { label: t.draws.statusResulted, variant: 'secondary' }
    case 'settled':
      return { label: t.draws.statusSettled, variant: 'default' }
    case 'cancelled':
      return { label: t.draws.statusCancelled, variant: 'outline' }
    case 'skipped':
      return { label: t.draws.statusSkipped, variant: 'warning' }
    case 'scheduled':
    default:
      return { label: t.draws.statusScheduled, variant: 'outline' }
  }
}

/**
 * True when a lottery announces a 2 ตัวล่าง. Derived from bet_config — any bet type that
 * reads the bottom number makes it meaningful, so switching any of them on is what makes
 * the field appear. Mirrors requiresBottomTwo() and BOTTOM_BET_TYPES in the API service;
 * the list has to stay identical, or the form stops offering a field the API then
 * refuses to accept the result without.
 */
const BOTTOM_BET_TYPES = ['2bottom', 'run_bottom', 'odd_even_bottom', 'high_low_bottom'] as const

function requiresBottomTwo(lottery: DrawLottery): boolean {
  return BOTTOM_BET_TYPES.some((t) => lottery.betConfig[t]?.enabled)
}

/** Small preview block: prizeFirst + bottomTwo (omitted for top-only). Reused in confirm dialogs. */
function ResultPreview({
  prizeFirst,
  bottomTwo,
  frontThree,
  bottomThree,
}: {
  prizeFirst: string
  bottomTwo: string | null
  frontThree?: string[] | null
  bottomThree?: string[] | null
}) {
  const t = useT()
  return (
    <div className="rounded-[var(--radius-sm)] border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs text-muted-foreground">
          {bottomTwo === null ? t.draws.topThree : t.draws.prizeFirst}
        </span>
        <span className="font-mono text-base font-bold tracking-widest text-foreground">
          {prizeFirst}
        </span>
      </div>
      {frontThree && (
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-xs text-muted-foreground">{t.draws.frontThree}</span>
          <span className="font-mono text-base font-bold tracking-widest text-foreground">
            {frontThree.join(' · ')}
          </span>
        </div>
      )}
      {bottomThree && (
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-xs text-muted-foreground">{t.draws.bottomThree}</span>
          <span className="font-mono text-base font-bold tracking-widest text-foreground">
            {bottomThree.join(' · ')}
          </span>
        </div>
      )}
      {bottomTwo !== null && (
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-xs text-muted-foreground">{t.draws.bottomTwo}</span>
          <span className="font-mono text-base font-bold tracking-widest text-foreground">
            {bottomTwo}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Phase-1 (enter) / correction (edit) form. No payout happens here, so there is
 * no confirm step — it just records / corrects the announced result.
 */
function ResultForm({
  lottery,
  draw,
  mode,
  onDone,
  onCancel,
}: {
  lottery: DrawLottery
  draw: DrawWithResult
  mode: FormMode
  onDone: (mode: FormMode) => void
  onCancel: () => void
}) {
  const t = useT()
  const [prizeFirst, setPrizeFirst] = useState(mode === 'edit' ? (draw.result?.prizeFirst ?? '') : '')
  const [bottomTwo, setBottomTwo] = useState(mode === 'edit' ? (draw.result?.bottomTwo ?? '') : '')
  // The Thai government lottery announces two of each 3-digit prize; shown only when the
  // lottery offers the bet that reads them (the API requires them exactly then).
  const needsFront = !!lottery.betConfig['3front']?.enabled
  const needsBottomThree = !!lottery.betConfig['3bottom']?.enabled
  const [frontThree, setFrontThree] = useState<[string, string]>(
    mode === 'edit' ? [draw.result?.frontThree?.[0] ?? '', draw.result?.frontThree?.[1] ?? ''] : ['', ''],
  )
  const [bottomThree, setBottomThree] = useState<[string, string]>(
    mode === 'edit' ? [draw.result?.bottomThree?.[0] ?? '', draw.result?.bottomThree?.[1] ?? ''] : ['', ''],
  )
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // หวยหุ้น announces the 3-digit top only — one field, no 2 ตัวล่าง.
  const needsBottom = requiresBottomTwo(lottery)
  // Exactly digit_count digits — that is what the field states the lottery announces
  // (หวยไทย six, หวย ธ.ก.ส./ออมสิน seven, หวยหุ้น three). Accepting a shorter number is
  // what let the first ธ.ก.ส./ออมสิน results be recorded three digits long; the API
  // refuses that now, and so does this.
  const digits = Math.max(3, lottery.digitCount)
  const prizeFirstValid = new RegExp(`^\\d{${digits}}$`).test(prizeFirst)
  const bottomTwoValid = BOTTOM_TWO_RE.test(bottomTwo)
  const threeValid = (pair: [string, string]) => pair.every((n) => THREE_DIGITS_RE.test(n))
  const canSubmit = prizeFirstValid && (!needsBottom || bottomTwoValid)
    && (!needsFront || threeValid(frontThree)) && (!needsBottomThree || threeValid(bottomThree))
  // Three digits means the lottery announces the top itself; longer means it announces
  // รางวัลที่ 1 and the top is its last three digits. Only the label differs.
  const topOnly = digits === 3
  const digitHint = (s: string) => s.replace('{n}', String(digits))
  // Built to length rather than sliced from a fixed literal — a '123456' constant was
  // shorter than digits for the seven-digit lotteries and silently under-hinted them.
  const placeholderDigits = Array.from({ length: digits }, (_, i) => (i + 1) % 10).join('')

  async function handleSubmit() {
    setSubmitting(true)
    setErr(null)
    try {
      const payload = {
        prizeFirst,
        bottomTwo: needsBottom ? bottomTwo : null,
        frontThree: needsFront ? frontThree : null,
        bottomThree: needsBottomThree ? bottomThree : null,
      }
      if (mode === 'edit') {
        await apiDrawUpdateResult(draw.id, payload)
      } else {
        await apiDrawEnterResult(draw.id, payload)
      }
      onDone(mode)
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.draws.submitError)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="p-4 bg-secondary/20 border-t border-border space-y-3">
      {err && (
        <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
          <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{err}</p>
        </div>
      )}

      {!needsBottom && (
        <p className="font-mono text-[10px] text-muted-foreground">{t.draws.topOnlyNote}</p>
      )}

      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          needsBottom || needsFront || needsBottomThree ? 'sm:grid-cols-2' : 'sm:max-w-xs',
        )}
      >
        <div className="flex flex-col gap-1">
          <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {/* A 3-digit lottery announces the top itself; longer ones announce รางวัลที่ 1
                and the top is derived from its last three digits. */}
            {topOnly ? t.draws.topThree : t.draws.prizeFirst}
          </Label>
          <Input
            inputMode="numeric"
            autoComplete="off"
            value={prizeFirst}
            onChange={(e) => setPrizeFirst(e.target.value.replace(/\D/g, '').slice(0, digits))}
            placeholder={placeholderDigits}
            aria-invalid={prizeFirst.length > 0 && !prizeFirstValid}
            className="font-mono text-sm h-9 tracking-widest"
          />
          <p
            className={cn(
              'font-mono text-[10px]',
              prizeFirst.length > 0 && !prizeFirstValid
                ? 'text-destructive'
                : 'text-muted-foreground',
            )}
          >
            {digitHint(
              prizeFirst.length > 0 && !prizeFirstValid
                ? t.draws.prizeFirstInvalidExact
                : t.draws.prizeFirstHintExact,
            )}
          </p>
        </div>

        {needsBottom && (
          <div className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.draws.bottomTwo}
            </Label>
            <Input
              inputMode="numeric"
              autoComplete="off"
              value={bottomTwo}
              onChange={(e) => setBottomTwo(e.target.value.replace(/\D/g, '').slice(0, 2))}
              placeholder="42"
              aria-invalid={bottomTwo.length > 0 && !bottomTwoValid}
              className="font-mono text-sm h-9 tracking-widest"
            />
            <p
              className={cn(
                'font-mono text-[10px]',
                bottomTwo.length > 0 && !bottomTwoValid
                  ? 'text-destructive'
                  : 'text-muted-foreground',
              )}
            >
              {bottomTwo.length > 0 && !bottomTwoValid
                ? t.draws.bottomTwoInvalid
                : t.draws.bottomTwoHint}
            </p>
          </div>
        )}

        {([
          { show: needsFront, label: t.draws.frontThree, value: frontThree, set: setFrontThree, id: 'front' },
          { show: needsBottomThree, label: t.draws.bottomThree, value: bottomThree, set: setBottomThree, id: 'bottom3' },
        ] as const).filter((f) => f.show).map((f) => {
          const filled = f.value.some((n) => n.length > 0)
          const bad = filled && !threeValid([f.value[0], f.value[1]])
          return (
            <div key={f.id} className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {f.label}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[0, 1].map((i) => (
                  <Input
                    key={i}
                    inputMode="numeric"
                    autoComplete="off"
                    aria-label={`${f.label} ${i + 1}`}
                    value={f.value[i]}
                    onChange={(e) => {
                      const next: [string, string] = [f.value[0], f.value[1]]
                      next[i] = e.target.value.replace(/\D/g, '').slice(0, 3)
                      f.set(next)
                    }}
                    placeholder="123"
                    aria-invalid={bad}
                    className="font-mono text-sm h-9 tracking-widest"
                  />
                ))}
              </div>
              <p className={cn('font-mono text-[10px]', bad ? 'text-destructive' : 'text-muted-foreground')}>
                {bad ? t.draws.threeInvalid : t.draws.threeHint}
              </p>
            </div>
          )
        })}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="h-8 font-mono text-xs"
          disabled={submitting}
        >
          {t.common.cancel}
        </Button>
        <Button
          size="sm"
          onClick={() => void handleSubmit()}
          className="h-8 font-mono text-xs gap-1.5"
          disabled={!canSubmit || submitting}
        >
          {submitting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Check className="h-3 w-3" />
          )}
          {submitting ? t.draws.submitting : mode === 'edit' ? t.draws.saveEdit : t.draws.submitResult}
        </Button>
      </div>
    </div>
  )
}

/** Phase-2 confirmation — pays out winners. Shows the result being settled. */
function SettleConfirmDialog({
  lottery,
  draw,
  onSettled,
  onClose,
}: {
  lottery: DrawLottery
  draw: DrawWithResult
  onSettled: (summary: DrawSettleSummary) => void
  onClose: () => void
}) {
  const t = useT()
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleConfirm() {
    setSubmitting(true)
    setErr(null)
    try {
      const summary = await apiDrawSettle(draw.id)
      onSettled(summary)
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.draws.settleError)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!submitting && !v) onClose()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {t.draws.settleConfirmTitle}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {t.draws.settleConfirmDesc}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-0.5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {lottery.name}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {t.draws.drawNumber} {draw.drawNumber}
          </span>
        </div>
        {draw.result && (
          <ResultPreview
            prizeFirst={draw.result.prizeFirst} bottomTwo={draw.result.bottomTwo}
            frontThree={draw.result.frontThree} bottomThree={draw.result.bottomThree}
          />
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 font-mono text-xs"
            disabled={submitting}
          >
            {t.common.cancel}
          </Button>
          <Button
            size="sm"
            onClick={() => void handleConfirm()}
            className="h-8 font-mono text-xs gap-1.5"
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Coins className="h-3 w-3" />
            )}
            {submitting ? t.draws.settling : t.draws.settleConfirmSubmit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Void confirmation — claws back payouts and returns the draw to `resulted`. */
/**
 * Sidebar clock for a lottery: how long until its งวด is announced, or what it is
 * waiting on once that moment has passed. Admins scan this column to find the
 * lottery that needs attention next, so "waiting" states read as actions.
 */
function drawClock(
  lottery: DrawLotteryWithCurrent,
  now: number,
  t: Dict,
): { text: string; tone: 'countdown' | 'due' | 'idle' } {
  const cur = lottery.currentDraw
  if (!cur) return { text: t.draws.clockNoDraw, tone: 'idle' }
  if (cur.status === 'resulted') return { text: t.draws.clockAwaitingPayout, tone: 'due' }

  const secs = Math.floor((new Date(cur.drawAt).getTime() - now) / 1000)
  if (secs <= 0) return { text: t.draws.clockAwaitingResult, tone: 'due' }

  const d = Math.floor(secs / 86400)
  const h = Math.floor((secs % 86400) / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (d > 0) return { text: `${d} ${t.draws.unitDay} ${h} ${t.draws.unitHour}`, tone: 'countdown' }
  if (h > 0) return { text: `${h} ${t.draws.unitHour} ${pad(m)} ${t.draws.unitMin}`, tone: 'countdown' }
  return { text: `${pad(m)}:${pad(s)}`, tone: 'countdown' }
}

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const

/**
 * Human summary of a schedule, e.g. "ทุกวัน 20:30 · ปิดรับก่อน 10 นาที".
 *
 * A lottery announcing several times a day lists every round: "จ อ พ พฤ ศ · 2 รอบ:
 * รอบเช้า 10:30 · รอบบ่าย 14:00 · ปิดรับก่อน 30 นาที". With one round the string is
 * byte-for-byte what it was before rounds existed.
 */
function describeSchedule(lottery: DrawLottery, t: Dict): string {
  const s = lottery.drawSchedule
  const mins = Math.round(lottery.betCloseBeforeSeconds / 60)
  const close = ` · ${t.draws.closeBeforeShort(mins)}`
  if (!s || s.type === 'manual') return t.draws.scheduleManual
  const rounds = normalizeRounds(s)
  const time = rounds.length > 1
    ? `${t.draws.scheduleRounds(rounds.length)}: ${describeRounds(rounds)}`
    : (rounds[0]?.time ?? '--:--')
  if (s.type === 'daily') return `${t.draws.scheduleDaily} ${time}${close}`
  if (s.type === 'weekly') {
    const days = (s.days ?? []).map((d) => t.draws.weekdayShort[d] ?? d).join(' ')
    return `${days} ${time}${close}`
  }
  const dates = (s.dates ?? []).join(', ')
  return `${t.draws.scheduleMonthlyOn(dates)} ${time}${close}`
}

/** Schedule + betting-window settings for one lottery. */
/**
 * The กติกา members read beside this lottery's payout table.
 *
 * Only the body is edited here — the payout rates on that page are generated from
 * `betConfig`, so an operator cannot write a rate that differs from the one the bet
 * endpoint enforces.
 *
 * What the API stores is the sanitised form of what was submitted, so the response is fed
 * back into the editor rather than the submitted string: if the allowlist dropped
 * something, the operator sees it go rather than finding out from a member.
 */
function RulesDialog({ lottery, onSaved, onCancel }: {
  lottery: DrawLottery
  onSaved: () => void
  onCancel: () => void
}) {
  const t = useT()
  const [html, setHtml] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [tab, setTab] = useState<'edit' | 'preview'>('edit')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    apiDrawRules(lottery.id)
      .then((r) => { if (!cancelled) { setHtml(r.html); setDirty(false) } })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiRequestError ? e.message : t.draws.rulesLoadError) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [lottery.id, t])

  async function handleSave() {
    setSaving(true)
    setErr(null)
    try {
      const saved = await apiDrawSaveRules(lottery.id, html)
      // The stored form, not what was sent — anything the allowlist removed is visible
      // straight away instead of silently differing from what members get.
      setHtml(saved.html)
      setDirty(false)
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.draws.rulesSaveError)
    } finally {
      setSaving(false)
    }
  }

  const tabCls = (active: boolean) => cn(
    'h-7 rounded-[var(--radius-sm)] px-3 font-mono text-xs transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
  )

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel() }}>
      {/* A rules body can run to hundreds of words plus images — well past what fits in
          one screen. DialogContent has no height cap of its own, so a body that tall
          used to push the dialog's top and bottom past the viewport with no way to
          reach either: nothing on the page scrolls a `position: fixed` element, so the
          title, the tabs and the footer's save button could all end up unreachable at
          once. Capped here and turned into a header/scroll-body/footer sandwich instead
          — the title, tabs and footer stay put, and only the body scrolls. */}
      <DialogContent className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t.draws.rulesTitle(lottery.name)}</DialogTitle>
          <DialogDescription>{t.draws.rulesHint}</DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 items-center gap-1">
          <button type="button" className={tabCls(tab === 'edit')} onClick={() => setTab('edit')}>
            {t.draws.rulesTabEdit}
          </button>
          <button type="button" className={tabCls(tab === 'preview')} onClick={() => setTab('preview')}>
            {t.draws.rulesTabPreview}
          </button>
        </div>

        {/* min-h-0 is load-bearing on a flex child: without it the browser refuses to
            shrink this below its content's natural height, and overflow-y-auto never
            gets the chance to kick in. */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <Skeleton className="h-[380px] w-full rounded-[var(--radius)]" />
          ) : tab === 'edit' ? (
            <RulesEditor
              lotteryId={lottery.id}
              value={html}
              disabled={saving}
              onChange={(next) => { setHtml(next); setDirty(true) }}
            />
          ) : (
            <div className="rounded-[var(--radius)] border border-border bg-card">
              <RulesPreview html={html} />
            </div>
          )}
        </div>

        {err && (
          <p role="alert" className="shrink-0 text-[0.8125rem] font-medium text-destructive">{err}</p>
        )}

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onCancel} disabled={saving}>{t.common.cancel}</Button>
          <Button onClick={() => void handleSave()} disabled={saving || loading || !dirty} className="gap-1.5">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SettingsDialog({
  lottery,
  onSaved,
  onCancel,
}: {
  lottery: DrawLottery
  onSaved: () => void
  onCancel: () => void
}) {
  const t = useT()
  const s = lottery.drawSchedule
  const [type, setType] = useState<DrawScheduleConfig['type']>(s?.type ?? 'manual')
  const [rounds, setRounds] = useState<RoundDraft[]>(() => roundDraftsFrom(s))
  const [days, setDays] = useState<number[]>(s?.days ?? [])
  const [dates, setDates] = useState<number[]>(s?.dates ?? [])
  const [closeMins, setCloseMins] = useState(String(Math.round(lottery.betCloseBeforeSeconds / 60)))
  // Two shapes cover what admins actually ask for: a round number of days (หวยไทย's
  // ten-day run-up, unrelated to any clock) and a fixed wall-clock time repeating every
  // day (หวยฮานอย "เปิดตี 1"). The stored seconds value doesn't say which produced it, so
  // openBeforeModeOf infers one — see its own comment for why that's safe.
  const initialOpenMode = openBeforeModeOf(lottery.betOpenBeforeSeconds)
  const [openMode, setOpenMode] = useState<OpenBeforeMode>(initialOpenMode)
  const [openDays, setOpenDays] = useState(
    initialOpenMode === 'days' ? String(Math.round((lottery.betOpenBeforeSeconds ?? 0) / 86400)) : '',
  )
  // Read against the first round as the form first opened — the same round the save path
  // uses (there `sorted[0]`), so a value round-trips even if nothing else changes.
  const firstRoundTimeInitial = roundDraftsFrom(s)[0]!.time
  const [openClock, setOpenClock] = useState(
    initialOpenMode === 'time'
      ? clockFromSecondsBefore(lottery.betOpenBeforeSeconds!, firstRoundTimeInitial)
      : '01:00',
  )
  // Advanced, off by default: opens a fixed span after the PREVIOUS round's own
  // announcement instead of relative to this round's own — see
  // betOpenAfterPreviousSeconds. The one case today is หวยหุ้นดาวโจนส์, whose 7-hour
  // close-before left no room for a same-round "ตี 1" offset — see 055's migration note.
  const [openAfterPreviousEnabled, setOpenAfterPreviousEnabled] = useState(
    lottery.betOpenAfterPreviousSeconds != null,
  )
  const [openAfterPreviousDuration, setOpenAfterPreviousDuration] = useState(
    lottery.betOpenAfterPreviousSeconds != null
      ? secondsToDuration(lottery.betOpenAfterPreviousSeconds)
      : '04:40',
  )
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const minsValid = /^\d{1,4}$/.test(closeMins) && Number(closeMins) <= 1440
  const openDaysValid = openMode !== 'days' || (openDays !== '' && Number(openDays) >= 0 && Number(openDays) <= 60)
  const openClockValid = openMode !== 'time' || isValidClock(openClock)
  const openAfterPreviousValid = !openAfterPreviousEnabled || isValidClock(openAfterPreviousDuration)
  // The cutoff is part of the check (a round cannot open after it closes), so the rounds
  // are only fully validated once it parses — until then the cutoff's own error stands.
  const roundsProblem = validateRounds(rounds, minsValid ? Number(closeMins) * 60 : undefined)
  const canSubmit =
    minsValid && openDaysValid && openClockValid && openAfterPreviousValid &&
    (type === 'manual' ||
      (roundsProblem === null &&
        (type !== 'weekly' || days.length > 0) &&
        (type !== 'monthly' || dates.length > 0)))

  const toggle = (list: number[], v: number) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v].sort((a, b) => a - b)

  const patchRound = (i: number, patch: Partial<RoundDraft>) =>
    setRounds((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  // What "opens at HH:MM" would mean right now — reactive to edits to the round times
  // themselves, so retyping the first round's time updates the preview underneath it.
  const liveRounds = toRoundConfigs(rounds)
  const firstRoundTime = liveRounds[0]?.time ?? firstRoundTimeInitial

  const problemAt = (i: number) => (roundsProblem?.index === i ? roundsProblem.kind : null)
  const problemText =
    roundsProblem?.kind === 'duplicate' ? t.draws.roundDuplicate
    : roundsProblem?.kind === 'label' ? t.draws.roundLabelRequired
    : roundsProblem?.kind === 'time' ? t.draws.roundTimeInvalid
    : roundsProblem?.kind === 'openTime' ? t.draws.roundOpenInvalid
    : roundsProblem?.kind === 'openAfterClose' ? t.draws.roundOpenAfterClose
    : roundsProblem?.kind === 'openAfterPrevTooLate' ? t.draws.roundOpenAfterPrevTooLate
    : null

  async function handleSubmit() {
    setSubmitting(true)
    setErr(null)
    try {
      const sorted = toRoundConfigs(rounds)
      const schedule: DrawScheduleConfig =
        type === 'manual'
          ? { type: 'manual' }
          : {
              type,
              rounds: sorted,
              // Mirrors the first round. The server derives this too; sending it keeps a
              // build that predates `rounds` able to read the schedule if we roll back.
              drawTime: sorted[0]!.time,
              tzOffsetHours: s?.tzOffsetHours ?? 7,
              ...(type === 'weekly' ? { days } : {}),
              ...(type === 'monthly' ? { dates } : {}),
            }
      const betOpenBeforeSeconds =
        openMode === 'off' ? null
        : openMode === 'days' ? Number(openDays) * 86400
        // Against sorted[0], not the lottery prop — if the admin also moved the first
        // round's time in this same save, the offset follows it rather than the stale value.
        : secondsBeforeFromClock(openClock, sorted[0]!.time)
      const betOpenAfterPreviousSeconds = openAfterPreviousEnabled
        ? durationToSeconds(openAfterPreviousDuration)
        : null
      await apiDrawUpdateSettings(lottery.id, {
        drawSchedule: schedule,
        betCloseBeforeSeconds: Number(closeMins) * 60,
        // null clears it back to the default rather than leaving the old value behind.
        betOpenBeforeSeconds,
        betOpenAfterPreviousSeconds,
      })
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.draws.settingsError)
    } finally {
      setSubmitting(false)
    }
  }

  const chip = (active: boolean) =>
    cn(
      'h-7 min-w-9 rounded-[var(--radius-sm)] border px-2 font-mono text-xs transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-muted-foreground hover:bg-muted',
    )

  return (
    <Dialog open onOpenChange={(v) => { if (!submitting && !v) onCancel() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {t.draws.settingsTitle} — {lottery.name}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {t.draws.settingsDesc}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex flex-col gap-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.draws.scheduleType}
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {(['daily', 'weekly', 'monthly', 'manual'] as const).map((v) => (
                <button key={v} type="button" className={chip(type === v)} onClick={() => setType(v)}>
                  {t.draws.scheduleTypeLabel[v]}
                </button>
              ))}
            </div>
          </div>

          {type === 'weekly' && (
            <div className="flex flex-col gap-1.5">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t.draws.scheduleDays}
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={chip(days.includes(d))}
                    onClick={() => setDays((prev) => toggle(prev, d))}
                    aria-pressed={days.includes(d)}
                  >
                    {t.draws.weekdayShort[d]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {type === 'monthly' && (
            <div className="flex flex-col gap-1.5">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t.draws.scheduleDates}
              </Label>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={cn(chip(dates.includes(d)), 'min-w-7 px-1')}
                    onClick={() => setDates((prev) => toggle(prev, d))}
                    aria-pressed={dates.includes(d)}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <p className="font-mono text-[10px] text-muted-foreground">{t.draws.scheduleDatesHint}</p>
            </div>
          )}

          {type !== 'manual' && (
            <div className="flex flex-col gap-1.5">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t.draws.scheduleTime}
              </Label>

              <div className="flex flex-col gap-1.5">
                {/* A single round keeps the bare row it always had — that is every
                    lottery but three. Several rounds each get their own block, because
                    the opening time below belongs to that round and a flat list of six
                    inputs gives no clue which one it belongs to. */}
                {rounds.map((r, i) => (
                  <div
                    key={i}
                    className={cn(
                      rounds.length > 1
                        && 'flex flex-col gap-1.5 rounded-[var(--radius-sm)] border border-border/70 bg-muted/20 p-2',
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="time"
                        value={r.time}
                        onChange={(e) => patchRound(i, { time: e.target.value })}
                        aria-label={t.draws.roundTimeOf(i + 1)}
                        aria-invalid={problemAt(i) === 'time' || problemAt(i) === 'duplicate'}
                        className="font-mono text-sm h-9 w-[7.5rem] shrink-0"
                      />
                      {rounds.length > 1 && (
                        <>
                          <Input
                            value={r.labelTh}
                            onChange={(e) => patchRound(i, { labelTh: e.target.value.slice(0, 24) })}
                            placeholder={t.draws.roundLabelPlaceholder}
                            aria-label={t.draws.roundLabelOf(i + 1)}
                            aria-invalid={problemAt(i) === 'label'}
                            className="font-mono text-sm h-9 flex-1 min-w-0"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setRounds((prev) => prev.filter((_, j) => j !== i))}
                            aria-label={t.draws.roundRemoveOf(i + 1)}
                            className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Only worth asking per round once there is more than one: with a
                        single round the lottery-wide "เวลาเปิด" below says the same
                        thing, and two fields for one setting is how they drift apart. */}
                    {rounds.length > 1 && (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {t.draws.roundOpenLabel}
                          </span>
                          {/* Three answers to "when does this round start taking bets",
                              because two of them cannot be written as the same kind of
                              value: a clock belongs to this round, a span belongs to the
                              previous round's announcement. */}
                          {(['inherit', 'time', 'afterPrev'] as const).map((m) => (
                            <button
                              key={m}
                              type="button"
                              className={chip(r.openMode === m)}
                              onClick={() => patchRound(i, { openMode: m })}
                              aria-pressed={r.openMode === m}
                            >
                              {m === 'inherit' ? t.draws.roundOpenModeInherit
                                : m === 'time' ? t.draws.roundOpenModeTime
                                : t.draws.roundOpenModeAfterPrev}
                            </button>
                          ))}
                        </div>

                        {r.openMode === 'inherit' && (
                          <p className="font-mono text-[10px] text-muted-foreground">
                            {t.draws.roundOpenInherit}
                          </p>
                        )}

                        {r.openMode === 'time' && (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <Input
                              type="time"
                              value={r.openClock}
                              onChange={(e) => patchRound(i, { openClock: e.target.value })}
                              aria-label={t.draws.roundOpenOf(i + 1)}
                              aria-invalid={problemAt(i) === 'openTime' || problemAt(i) === 'openAfterClose'}
                              className="font-mono text-sm h-8 w-[7.5rem] shrink-0"
                            />
                            <span
                              className={cn(
                                'font-mono text-[10px]',
                                problemAt(i) === 'openAfterClose' ? 'text-destructive' : 'text-muted-foreground',
                              )}
                            >
                              {isValidClock(r.openClock) && isValidClock(r.time)
                                ? t.draws.roundOpenComputed(formatDurationTh(
                                    Math.round(secondsBeforeFromClock(r.openClock, r.time) / 60),
                                  ))
                                : t.draws.roundOpenInvalid}
                            </span>
                          </div>
                        )}

                        {r.openMode === 'afterPrev' && (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            {/* The HH:MM widget reads as a duration here, not a clock —
                                same double duty the lottery-wide rule below makes of it. */}
                            <Input
                              type="time"
                              value={r.openAfterPrev}
                              onChange={(e) => patchRound(i, { openAfterPrev: e.target.value })}
                              aria-label={t.draws.roundOpenAfterPrevOf(i + 1)}
                              aria-invalid={
                                problemAt(i) === 'openTime' || problemAt(i) === 'openAfterPrevTooLate'
                              }
                              className="font-mono text-sm h-8 w-[7.5rem] shrink-0"
                            />
                            <span
                              className={cn(
                                'font-mono text-[10px]',
                                problemAt(i) === 'openAfterPrevTooLate'
                                  ? 'text-destructive' : 'text-muted-foreground',
                              )}
                            >
                              {!isValidClock(r.openAfterPrev) ? t.draws.roundOpenAfterPrevHint
                                : durationToSeconds(r.openAfterPrev) === 0
                                  ? t.draws.roundOpenAfterPrevNow
                                  : t.draws.roundOpenAfterPrevComputed(formatDurationTh(
                                      Math.round(durationToSeconds(r.openAfterPrev) / 60),
                                    ))}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRounds((prev) => [...prev, newRoundDraft()])}
                  disabled={rounds.length >= MAX_ROUNDS}
                  className="h-7 font-mono text-[11px] gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {t.draws.roundAdd}
                </Button>
                {rounds.length >= MAX_ROUNDS && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {t.draws.roundsMax(MAX_ROUNDS)}
                  </span>
                )}
              </div>

              {problemText ? (
                <p className="font-mono text-[10px] text-destructive">{problemText}</p>
              ) : (
                <p className="font-mono text-[10px] text-muted-foreground">
                  {rounds.length > 1
                    ? t.draws.roundsPreview(describeRounds(toRoundConfigs(rounds)))
                    : t.draws.scheduleTimeHint}
                </p>
              )}
              {rounds.length > 1 && (
                <p className="font-mono text-[10px] text-muted-foreground">{t.draws.roundsChangeNote}</p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.draws.closeBefore}
            </Label>
            <Input
              inputMode="numeric"
              value={closeMins}
              onChange={(e) => setCloseMins(e.target.value.replace(/\D/g, '').slice(0, 4))}
              aria-invalid={!minsValid}
              className="font-mono text-sm h-9 sm:max-w-[10rem]"
            />
            <p className={cn('font-mono text-[10px]', minsValid ? 'text-muted-foreground' : 'text-destructive')}>
              {t.draws.closeBeforeHint}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.draws.openBefore}
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {(['off', 'days', 'time'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={chip(openMode === m)}
                  onClick={() => setOpenMode(m)}
                  aria-pressed={openMode === m}
                >
                  {m === 'off' ? t.draws.openBeforeOff : m === 'days' ? t.draws.openBeforeDays : t.draws.openBeforeTime}
                </button>
              ))}
            </div>

            {openMode === 'off' && (
              <p className="font-mono text-[10px] text-muted-foreground">{t.draws.openBeforeOffHint}</p>
            )}

            {openMode === 'days' && (
              <>
                <Input
                  inputMode="numeric"
                  placeholder={t.draws.openBeforePlaceholder}
                  value={openDays}
                  onChange={(e) => setOpenDays(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
                  aria-invalid={!openDaysValid}
                  className="font-mono text-sm h-9 sm:max-w-[10rem]"
                />
                <p className={cn('font-mono text-[10px]', openDaysValid ? 'text-muted-foreground' : 'text-destructive')}>
                  {t.draws.openBeforeDaysHint}
                </p>
              </>
            )}

            {openMode === 'time' && (
              <>
                <Input
                  type="time"
                  value={openClock}
                  onChange={(e) => setOpenClock(e.target.value)}
                  aria-invalid={!openClockValid}
                  className="font-mono text-sm h-9 w-[7.5rem]"
                />
                <p className={cn('font-mono text-[10px]', openClockValid ? 'text-muted-foreground' : 'text-destructive')}>
                  {t.draws.openBeforeTimeHint}
                </p>
                {openClockValid && (
                  <p className="font-mono text-[10px] text-muted-foreground">
                    {t.draws.openBeforeTimeComputed(
                      formatDurationTh(Math.round(secondsBeforeFromClock(openClock, firstRoundTime) / 60)),
                    )}
                    {liveRounds.length > 1 && ` · ${t.draws.openBeforeTimeRoundNote(firstRoundTime)}`}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Advanced, off by default — the one lottery that needs it today
              (หวยหุ้นดาวโจนส์) has an unusually long ปิดรับก่อนออกผล, which the
              same-round "เวลาเปิด" above cannot satisfy on its own; see 055's
              migration note. Left collapsed to a single checkbox so it stays out
              of the way for every other lottery, which never touches it. */}
          <div className="flex flex-col gap-1.5 border-t border-border/50 pt-3">
            <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <input
                type="checkbox"
                checked={openAfterPreviousEnabled}
                onChange={(e) => setOpenAfterPreviousEnabled(e.target.checked)}
                className="h-3 w-3 accent-[var(--primary)]"
              />
              {t.draws.openAfterPrevious}
            </label>
            {openAfterPreviousEnabled && (
              <>
                <Input
                  type="time"
                  value={openAfterPreviousDuration}
                  onChange={(e) => setOpenAfterPreviousDuration(e.target.value)}
                  aria-invalid={!openAfterPreviousValid}
                  className="font-mono text-sm h-9 w-[7.5rem]"
                />
                <p className={cn(
                  'font-mono text-[10px]',
                  openAfterPreviousValid ? 'text-muted-foreground' : 'text-destructive',
                )}>
                  {t.draws.openAfterPreviousHint}
                </p>
                {openAfterPreviousValid && (
                  <p className="font-mono text-[10px] text-muted-foreground">
                    {t.draws.openAfterPreviousComputed(
                      formatDurationTh(Math.round(durationToSeconds(openAfterPreviousDuration) / 60)),
                    )}
                  </p>
                )}
              </>
            )}
          </div>

          {type === 'manual' && (
            <p className="font-mono text-[10px] text-muted-foreground">{t.draws.scheduleManualHint}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} className="h-8 font-mono text-xs" disabled={submitting}>
            {t.common.cancel}
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSubmit()}
            className="h-8 font-mono text-xs gap-1.5"
            disabled={!canSubmit || submitting}
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            {submitting ? t.draws.submitting : t.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** `<input type="datetime-local">` wants local wall time with no zone or seconds. */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * Open the next งวด. The times come from the lottery's schedule — the dialog shows
 * what they will be and asks for one confirmation. Only lotteries with no timetable
 * (or a deliberate one-off) fall back to typing them, behind "แก้เวลาเอง".
 */
function CreateDrawDialog({
  lottery,
  onCreated,
  onCancel,
  /**
   * Set when the dialog opened by itself, right after a งวด ended without a payout.
   * Cancelling and งดออกผล both skip the settlement that would otherwise have opened the
   * next งวด, so this offers to — and says why it is asking, because a dialog that
   * appears unbidden is otherwise indistinguishable from a misclick.
   */
  after,
}: {
  lottery: DrawLottery
  onCreated: () => void
  onCancel: () => void
  after?: 'cancelled' | 'skipped'
}) {
  const t = useT()
  const [slot, setSlot] = useState<DrawSlot | null>(null)
  const [loading, setLoading] = useState(true)
  const [manual, setManual] = useState(false)
  const [times, setTimes] = useState({ openAt: '', closeAt: '', drawAt: '' })
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Ask the server what the schedule points at — same maths that will run on submit.
  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const { slot: s } = await apiDrawNextSlot(lottery.id)
        if (!alive) return
        setSlot(s)
        const base = s ?? {
          openAt: new Date().toISOString(),
          closeAt: new Date(Date.now() + 3_600_000).toISOString(),
          drawAt: new Date(Date.now() + 3_600_000).toISOString(),
        }
        setTimes({
          openAt: toLocalInput(new Date(base.openAt)),
          closeAt: toLocalInput(new Date(base.closeAt)),
          drawAt: toLocalInput(new Date(base.drawAt)),
        })
        if (!s) setManual(true) // no timetable — nothing to confirm, so edit directly
      } catch (e) {
        if (alive) setErr(e instanceof ApiRequestError ? e.message : t.draws.createError)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [lottery.id, t])

  const openAt = new Date(times.openAt)
  const closeAt = new Date(times.closeAt)
  const drawAt = new Date(times.drawAt)
  const orderValid = openAt < closeAt && closeAt <= drawAt && !Number.isNaN(drawAt.getTime())
  const canSubmit = !loading && (manual ? orderValid : slot !== null)

  async function handleSubmit() {
    setSubmitting(true)
    setErr(null)
    try {
      await apiDrawCreate(
        lottery.id,
        manual
          ? { drawAt: drawAt.toISOString(), openAt: openAt.toISOString(), closeAt: closeAt.toISOString() }
          : undefined,
      )
      onCreated()
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.draws.createError)
    } finally {
      setSubmitting(false)
    }
  }

  const field = (key: 'openAt' | 'closeAt' | 'drawAt', label: string, hint: string) => (
    <div className="flex flex-col gap-1">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </Label>
      <Input
        type="datetime-local"
        value={times[key]}
        onChange={(e) => setTimes((prev) => ({ ...prev, [key]: e.target.value }))}
        className="font-mono text-sm h-9"
      />
      <p className="font-mono text-[10px] text-muted-foreground">{hint}</p>
    </div>
  )

  const summaryRow = (label: string, iso: string) => (
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono text-[11px] text-muted-foreground">{label}</span>
      <span className="font-mono text-xs font-semibold text-foreground tabular-nums">
        {format(new Date(iso), 'dd/MM/yyyy HH:mm')}
      </span>
    </div>
  )

  return (
    <Dialog open onOpenChange={(v) => { if (!submitting && !v) onCancel() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {after ? t.draws.createAfterEnding : t.draws.createTitle}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {manual ? t.draws.createDescManual : t.draws.createDesc}
          </DialogDescription>
        </DialogHeader>

        {after && (
          <div className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-border bg-secondary/40 p-2.5">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="font-mono text-[11px] leading-relaxed text-muted-foreground">
              {after === 'cancelled' ? t.draws.createAfterCancelNote : t.draws.createAfterSkipNote}
            </p>
          </div>
        )}

        {err && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : manual ? (
          <div className="space-y-3">
            {field('openAt', t.draws.openAt, t.draws.openAtHint)}
            {field('closeAt', t.draws.closeBetting, t.draws.closeAtHint)}
            {field('drawAt', t.draws.drawAt, t.draws.drawAtHint)}
            {!orderValid && (
              <p className="font-mono text-[10px] text-destructive">{t.draws.createOrderInvalid}</p>
            )}
          </div>
        ) : slot ? (
          <div className="space-y-2 rounded-[var(--radius-sm)] border border-border bg-card p-3">
            {slot.roundLabel && (
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[11px] text-muted-foreground">{t.draws.round}</span>
                <span className="font-mono text-xs font-semibold text-foreground">
                  {slot.roundLabel}
                </span>
              </div>
            )}
            {summaryRow(t.draws.openAt, slot.openAt)}
            {summaryRow(t.draws.closeBetting, slot.closeAt)}
            {summaryRow(t.draws.drawAt, slot.drawAt)}
            <p className="pt-1 font-mono text-[10px] text-muted-foreground">
              {describeSchedule(lottery, t)}
            </p>
          </div>
        ) : null}

        {!loading && !manual && (
          <button
            type="button"
            className="self-start font-mono text-[11px] text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[var(--radius-sm)]"
            onClick={() => setManual(true)}
          >
            {t.draws.createEditTimes}
          </button>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="h-8 font-mono text-xs"
            disabled={submitting}
          >
            {t.common.cancel}
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSubmit()}
            className="h-8 font-mono text-xs gap-1.5"
            disabled={!canSubmit || submitting}
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <CalendarPlus className="h-3 w-3" />
            )}
            {submitting ? t.draws.creating : t.draws.createSubmit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Confirm stopping bets early. Irreversible in the sense that a closed draw does not
 * reopen — anyone mid-slip loses the chance to submit — so it asks first.
 */
function CloseConfirmDialog({
  lottery,
  draw,
  onClosed,
  onCancel,
}: {
  lottery: DrawLottery
  draw: DrawWithResult
  onClosed: () => void
  onCancel: () => void
}) {
  const t = useT()
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleConfirm() {
    setSubmitting(true)
    setErr(null)
    try {
      await apiDrawClose(draw.id)
      onClosed()
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.draws.closeError)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!submitting && !v) onCancel() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {t.draws.closeConfirmTitle}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {t.draws.closeConfirmDesc}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-0.5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {lottery.name}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {t.draws.drawNumber} {draw.drawNumber}
          </span>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="h-8 font-mono text-xs"
            disabled={submitting}
          >
            {t.common.cancel}
          </Button>
          <Button
            size="sm"
            onClick={() => void handleConfirm()}
            className="h-8 font-mono text-xs gap-1.5"
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Lock className="h-3 w-3" />
            )}
            {submitting ? t.draws.closing : t.draws.closeConfirmSubmit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function VoidConfirmDialog({
  lottery,
  draw,
  onProceed,
  onClose,
}: {
  lottery: DrawLottery
  draw: DrawWithResult
  /** Hand off to the code dialog — clawing payouts back is never a one-click action. */
  onProceed: () => void
  onClose: () => void
}) {
  const t = useT()
  const submitting = false
  const err: string | null = null

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!submitting && !v) onClose()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {t.draws.voidConfirmTitle}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {t.draws.voidConfirmDesc}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        <div className="flex items-start gap-2 p-2.5 border border-warning/40 rounded-[var(--radius-sm)] bg-warning/10">
          <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {lottery.name}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {t.draws.drawNumber} {draw.drawNumber}
              </span>
            </div>
            {draw.result && (
              <p className="font-mono text-xs text-foreground mt-1 tracking-widest">
                {draw.result.prizeFirst}
                {draw.result.bottomTwo !== null && ` · ${draw.result.bottomTwo}`}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 font-mono text-xs"
            disabled={submitting}
          >
            {t.common.cancel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onProceed}
            className="h-8 font-mono text-xs gap-1.5"
          >
            <Undo2 className="h-3 w-3" />
            {t.draws.voidConfirmSubmit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Cancel a งวด that will never have a result and refund every bet on it. Asks for a
 * reason because this hands money back and the audit trail has to say why, and states
 * the amount at stake before the admin commits.
 */
/**
 * Cancel and งดออกผล are the same conversation — "this งวด pays nobody, here is why" —
 * and the same request shape, so one dialog serves both rather than a near-copy that
 * drifts. `mode` picks the wording and which endpoint it calls; everything else,
 * including the mandatory reason, is identical.
 */
function CancelDrawDialog({ draw, lottery, mode = 'cancel', onProceed, onClose }: {
  draw: DrawWithResult
  lottery: DrawLottery
  mode?: 'cancel' | 'skip'
  /** Hands the typed reason to the code dialog, which is what actually refunds. */
  onProceed: (reason: string) => void
  onClose: () => void
}) {
  const t = useT()
  const isSkip = mode === 'skip'
  const [reason, setReason] = useState('')
  const submitting = false
  const err: string | null = null

  return (
    <Dialog open onOpenChange={(v) => { if (!submitting && !v) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {isSkip ? t.draws.skipTitle : t.draws.cancelTitle}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {isSkip ? t.draws.skipDesc : t.draws.cancelDesc}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        <div className="flex items-start gap-2 p-2.5 border border-warning/40 rounded-[var(--radius-sm)] bg-warning/10">
          <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {lottery.name}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {t.draws.drawNumber} {draw.drawNumber}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="cancel-reason" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {t.draws.cancelReason}
          </Label>
          <Input
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            className="h-9 font-mono text-sm"
          />
          <p className="font-mono text-[10px] text-muted-foreground">{t.draws.cancelReasonHint}</p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="h-8 font-mono text-xs" disabled={submitting}>
            {t.common.cancel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onProceed(reason.trim())}
            className="h-8 font-mono text-xs gap-1.5"
            disabled={reason.trim().length < 3}
          >
            {isSkip ? <CalendarOff className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
            {isSkip ? t.draws.skipConfirm : t.draws.cancelConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function AdminDrawsPage() {
  const t = useT()
  const can = useCan()
  // Creating a lottery is reserved for accounts that hold everything — there is no
  // permission key that grants it, so it is checked as "unrestricted", not "allowed".
  const { permissions } = useAuth()
  const isSuperAdmin = permissions === null

  const [lotteries, setLotteries] = useState<DrawLotteryWithCurrent[]>([])
  // Drives the sidebar countdowns; one interval for the whole list.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const [lotteriesLoading, setLotteriesLoading] = useState(true)
  const [lotteriesError, setLotteriesError] = useState<string | null>(null)

  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [draws, setDraws] = useState<DrawWithResult[]>([])
  const [drawsLoading, setDrawsLoading] = useState(false)
  const [drawsError, setDrawsError] = useState<string | null>(null)

  // Which draw's inline result form (enter or edit) is expanded.
  const [expandedDrawId, setExpandedDrawId] = useState<string | null>(null)
  // Page-level confirm dialogs.
  const [closeTarget, setCloseTarget] = useState<DrawWithResult | null>(null)
  // Reopen has no confirm dialog — it's the corrective direction after an early close,
  // so the fastest path back is the point (a busy flag per row is enough to stop a
  // second click while the first is in flight).
  const [reopenBusyId, setReopenBusyId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  // Set only when the dialog opened by itself after a งวด ended without a payout.
  const [createAfter, setCreateAfter] = useState<'cancelled' | 'skipped' | undefined>(undefined)
  const [addOpen, setAddOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [rulesOpen, setRulesOpen] = useState(false)
  const [marketBusy, setMarketBusy] = useState(false)
  const [activeBusyId, setActiveBusyId] = useState<string | null>(null)
  const [settleTarget, setSettleTarget] = useState<DrawWithResult | null>(null)
  const [voidTarget, setVoidTarget] = useState<DrawWithResult | null>(null)
  const [cancelTarget, setCancelTarget] = useState<DrawWithResult | null>(null)
  const [skipTarget, setSkipTarget] = useState<DrawWithResult | null>(null)
  // The three actions that move money backwards wait for a code from the group. The
  // reason is snapshotted with the target: it is part of what the code authorises.
  const [otpVoid, setOtpVoid] = useState<DrawWithResult | null>(null)
  const [otpRefund, setOtpRefund] = useState<
    { draw: DrawWithResult; mode: 'cancel' | 'skip'; reason: string } | null
  >(null)
  // Success banner message (prebuilt so a single banner covers all four actions).
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const loadLotteries = useCallback(async () => {
    setLotteriesLoading(true)
    setLotteriesError(null)
    try {
      const data = await apiDrawGetLotteries()
      setLotteries(data)
      setSelectedId((prev) => prev ?? data[0]?.id ?? null)
    } catch (e) {
      setLotteriesError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setLotteriesLoading(false)
    }
  }, [t])

  const loadDraws = useCallback(
    async (lotteryId: string) => {
      setDrawsLoading(true)
      setDrawsError(null)
      try {
        const data = await apiDrawGetDraws(lotteryId)
        setDraws(data.draws)
      } catch (e) {
        setDrawsError(e instanceof ApiRequestError ? e.message : t.common.error)
      } finally {
        setDrawsLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    void loadLotteries()
  }, [loadLotteries])

  useEffect(() => {
    if (selectedId) void loadDraws(selectedId)
  }, [selectedId, loadDraws])

  const selectedLottery = lotteries.find((l) => l.id === selectedId) ?? null

  /**
   * Hidden lotteries sink to the bottom of the list.
   *
   * The server orders by how soon each one draws, which put a switched-off lottery at the
   * very top whenever its stale timetable said "soonest" — the first thing an admin saw
   * on a page about live งวด was one nobody can bet on. Sorting is stable, so the order
   * inside each group is still the server's.
   */
  const listedLotteries = useMemo(
    () => [...lotteries].sort((a, b) => Number(b.isActive) - Number(a.isActive)),
    [lotteries],
  )

  function handleSelect(id: string) {
    if (id === selectedId) return
    setSelectedId(id)
    setExpandedDrawId(null)
    setCloseTarget(null)
    setSettleTarget(null)
    setVoidTarget(null)
    closeCreate()
    setSettingsOpen(false)
    setSuccessMsg(null)
  }

  /** Any state change (result, payout, new งวด) also moves the sidebar clocks/order. */
  function refresh() {
    if (selectedId) void loadDraws(selectedId)
    void loadLotteries()
  }

  function handleFormDone(mode: FormMode) {
    setSuccessMsg(mode === 'edit' ? t.draws.editSuccess : t.draws.enterSuccess)
    setExpandedDrawId(null)
    refresh()
  }

  function handleClosed() {
    setCloseTarget(null)
    setSuccessMsg(t.draws.closeSuccess)
    refresh()
  }

  async function handleReopen(draw: DrawWithResult) {
    setReopenBusyId(draw.id)
    try {
      await apiDrawReopen(draw.id)
      setSuccessMsg(t.draws.reopenSuccess)
      refresh()
    } catch (e) {
      setDrawsError(e instanceof ApiRequestError ? e.message : t.draws.reopenError)
    } finally {
      setReopenBusyId(null)
    }
  }

  function handleCreated() {
    closeCreate()
    setSuccessMsg(t.draws.createSuccess)
    refresh()
  }

  /** Show/hide a lottery for members. Optimistic — the row reflects the new state at once. */
  async function toggleActive(lottery: DrawLotteryWithCurrent) {
    setActiveBusyId(lottery.id)
    try {
      const next = !lottery.isActive
      await apiDrawSetActive(lottery.id, next)
      setLotteries((prev) =>
        prev.map((l) => (l.id === lottery.id ? { ...l, isActive: next } : l)),
      )
    } catch (e) {
      setLotteriesError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setActiveBusyId(null)
    }
  }

  async function toggleMarket() {
    if (!selectedLottery) return
    setMarketBusy(true)
    try {
      const next = !selectedLottery.isMarketOpen
      await apiDrawSetMarket(selectedLottery.id, next)
      setLotteries((prev) =>
        prev.map((l) => (l.id === selectedLottery.id ? { ...l, isMarketOpen: next } : l)),
      )
    } catch (e) {
      setDrawsError(e instanceof ApiRequestError ? e.message : t.draws.marketToggleError)
    } finally {
      setMarketBusy(false)
    }
  }

  // A new งวด may only be opened while nothing is still taking bets. Keep the blocking
  // draw around so the button can say *why* it is disabled instead of vanishing.
  //
  // This mirrors findBettableDraw on the server, which is the rule that actually
  // decides. The stricter "nothing unsettled" version this replaces was never the real
  // rule: the API has always allowed the next งวด while the previous one waits for its
  // result, and the worker's auto-open sweep has been doing exactly that nightly. On a
  // lottery announcing twice a day the difference stops being cosmetic — the รอบเช้า
  // sits unsettled for hours, and the old rule would refuse to open the รอบบ่าย at all.
  const blockingDraw = draws.find(
    (d) => (d.status === 'scheduled' || d.status === 'open') && new Date(d.closeAt).getTime() > now,
  ) ?? null

  function handleSettled(summary: DrawSettleSummary) {
    setSettleTarget(null)
    setSuccessMsg(t.draws.resultSummary(summary.settled, summary.won, summary.lost))
    setExpandedDrawId(null)
    refresh()
  }

  function handleVoided(summary: DrawVoidSummary) {
    setVoidTarget(null)
    setSuccessMsg(t.draws.voidSummary(summary.reversed, summary.totalReturned))
    setExpandedDrawId(null)
    refresh()
  }

  /**
   * Both endings skip the payout that opens the next งวด, so the create dialog is
   * offered straight away rather than leaving the lottery with nothing to bet on until
   * the auto-open lull expires. Offered, not done: whether the next งวด should start
   * now is exactly the judgement the admin just made about this one.
   */
  function handleCancelled(summary: DrawCancelSummary) {
    setCancelTarget(null)
    setSuccessMsg(t.draws.cancelDone(summary.refunded, summary.totalRefunded))
    setExpandedDrawId(null)
    refresh()
    setCreateAfter('cancelled')
    setCreateOpen(true)
  }

  function handleSkipped(summary: DrawCancelSummary) {
    setSkipTarget(null)
    setSuccessMsg(t.draws.skipDone(summary.refunded, summary.totalRefunded))
    setExpandedDrawId(null)
    refresh()
    setCreateAfter('skipped')
    setCreateOpen(true)
  }

  function closeCreate() {
    setCreateOpen(false)
    setCreateAfter(undefined)
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.draws.title}
        </span>
      </div>

      {successMsg && (
        <div
          className="flex items-center gap-2 p-3 border border-success/40 rounded-[var(--radius)] bg-success/10 mb-4"
          role="status"
        >
          <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
          <p className="font-mono text-xs text-success flex-1">{successMsg}</p>
          <button
            type="button"
            onClick={() => setSuccessMsg(null)}
            aria-label={t.common.close}
            className="text-success/70 hover:text-success transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {lotteriesError && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive flex-1">{lotteriesError}</p>
          <Button
            variant="outline"
            size="sm"
            className="h-7 font-mono text-xs"
            onClick={() => void loadLotteries()}
          >
            {t.common.retry}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,18rem)_1fr] gap-4">
        {/* Lottery list */}
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] self-start">
          <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
            <Ticket className="h-3.5 w-3.5 text-secondary-foreground/60" />
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.draws.lotteryList}
            </span>
            {!lotteriesLoading && (
              <span className="font-mono text-xs text-secondary-foreground/60 ml-auto">
                {lotteries.length}
              </span>
            )}
            {/* Only an unrestricted admin creates a lottery, and only with a code from
                the Telegram group. The button is hidden for everyone else rather than
                shown and refused — the server enforces it either way. */}
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAddOpen(true)}
                className="ml-auto h-6 gap-1 font-mono text-[11px] text-secondary-foreground hover:bg-secondary-foreground/10"
              >
                <Plus className="h-3 w-3" />
                {t.draws.addLottery}
              </Button>
            )}
          </div>

          {lotteriesLoading ? (
            <div className="divide-y divide-border">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="ml-auto h-5 w-12" />
                </div>
              ))}
            </div>
          ) : lotteries.length === 0 ? (
            <div className="py-10 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.draws.noLotteries}</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {listedLotteries.map((l) => {
                const active = l.id === selectedId
                return (
                  <li
                    key={l.id}
                    className={cn(
                      'flex items-center transition-colors',
                      active ? 'bg-primary/8' : 'hover:bg-muted',
                      // A hidden lottery still lists here (admin-only) but reads as off.
                      !l.isActive && 'opacity-60',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(l.id)}
                      aria-current={active ? 'true' : undefined}
                      className={cn(
                        'flex min-w-0 flex-1 items-center gap-2 py-3 pl-4 pr-2 text-left',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            'font-mono text-sm truncate',
                            active ? 'text-primary font-semibold' : 'text-foreground',
                          )}
                        >
                          {l.name}
                        </p>
                        <p className="font-mono text-[10px] text-muted-foreground mt-0.5">
                          {l.code} ·{' '}
                          {l.category === 'stock' ? t.draws.categoryStock : t.draws.categoryDraw}
                        </p>
                      </div>
                      {(() => {
                        const clock = drawClock(l, now, t)
                        return (
                          <span
                            className={cn(
                              'font-mono text-[11px] tabular-nums shrink-0 text-right',
                              clock.tone === 'due' && 'font-semibold text-warning',
                              clock.tone === 'countdown' && 'text-foreground',
                              clock.tone === 'idle' && 'text-muted-foreground/60',
                            )}
                            title={l.currentDraw ? t.draws.drawAt : undefined}
                          >
                            {clock.text}
                          </span>
                        )
                      })()}
                    </button>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={l.isActive}
                      aria-label={`${l.name} — ${l.isActive ? t.common.active : t.common.inactive}`}
                      disabled={activeBusyId === l.id}
                      onClick={() => void toggleActive(l)}
                      className={cn(
                        'mr-3 shrink-0 rounded-full border px-2 py-0.5 font-mono text-[10px] transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        'disabled:opacity-50',
                        l.isActive
                          ? 'border-success/40 bg-success/10 text-success hover:bg-success/20'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {activeBusyId === l.id
                        ? '…'
                        : l.isActive ? t.common.active : t.common.inactive}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Draws panel */}
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] self-start">
          <div className="px-4 py-2.5 bg-secondary flex items-center justify-between gap-2">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground truncate">
              {selectedLottery
                ? `${t.draws.recentDraws} — ${selectedLottery.name}`
                : t.draws.recentDraws}
            </span>
            {selectedLottery && !drawsLoading && (
              <div className="flex items-center gap-2 shrink-0">
                {can('draws:config') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 font-mono text-xs gap-1.5"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings2 className="h-3 w-3" />
                  {t.draws.settings}
                </Button>
                )}
                {can('draws:config') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 font-mono text-xs gap-1.5"
                  onClick={() => setRulesOpen(true)}
                >
                  <BookText className="h-3 w-3" />
                  {t.draws.rules}
                </Button>
                )}
                {can('draws:config') && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-7 font-mono text-xs gap-1.5',
                    !selectedLottery.isMarketOpen && 'text-destructive hover:text-destructive',
                  )}
                  onClick={() => void toggleMarket()}
                  disabled={marketBusy}
                  aria-pressed={selectedLottery.isMarketOpen}
                >
                  {marketBusy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : selectedLottery.isMarketOpen ? (
                    <LockOpen className="h-3 w-3" />
                  ) : (
                    <Lock className="h-3 w-3" />
                  )}
                  {selectedLottery.isMarketOpen ? t.draws.marketOpen : t.draws.marketClosed}
                </Button>
                )}
                {can('draws:open') && (
                <Button
                  size="sm"
                  className="h-7 font-mono text-xs gap-1.5"
                  onClick={() => setCreateOpen(true)}
                  disabled={blockingDraw !== null}
                  title={
                    blockingDraw
                      ? t.draws.createBlocked(
                          blockingDraw.drawNumber,
                          format(new Date(blockingDraw.closeAt), 'dd/MM HH:mm'),
                        )
                      : undefined
                  }
                >
                  <CalendarPlus className="h-3 w-3" />
                  {t.draws.createDraw}
                </Button>
                )}
                <span className="font-mono text-xs text-secondary-foreground/60">
                  {draws.length}
                </span>
              </div>
            )}
          </div>

          {selectedLottery && !drawsLoading && (
            <div className="flex items-center gap-2 px-4 py-1.5 border-b border-border bg-card">
              <CalendarClock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <p className="font-mono text-[11px] text-muted-foreground">
                {describeSchedule(selectedLottery, t)}
              </p>
            </div>
          )}

          {selectedLottery && !drawsLoading && blockingDraw && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/40">
              <AlertCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <p className="font-mono text-[11px] text-muted-foreground">
                {t.draws.createBlocked(
                  blockingDraw.drawNumber,
                  format(new Date(blockingDraw.closeAt), 'dd/MM HH:mm'),
                )}
              </p>
            </div>
          )}

          {!selectedLottery && !lotteriesLoading ? (
            <div className="py-12 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.draws.selectLottery}</p>
            </div>
          ) : drawsError ? (
            <div className="flex items-center gap-2 p-4">
              <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
              <p className="font-mono text-xs text-destructive flex-1">{drawsError}</p>
              <Button
                variant="outline"
                size="sm"
                className="h-7 font-mono text-xs"
                onClick={() => selectedId && void loadDraws(selectedId)}
              >
                {t.common.retry}
              </Button>
            </div>
          ) : drawsLoading || lotteriesLoading ? (
            <div className="divide-y divide-border">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="ml-auto h-7 w-32" />
                </div>
              ))}
            </div>
          ) : draws.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.draws.noDraws}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.draws.drawNumber}</TableHead>
                  <TableHead>{t.draws.drawAt}</TableHead>
                  <TableHead>{t.draws.status}</TableHead>
                  <TableHead>{t.draws.result}</TableHead>
                  <TableHead>{t.draws.closeAt}</TableHead>
                  <TableHead className="text-right">{t.common.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draws.map((d) => {
                  const meta = statusMeta(t, d.status)
                  const expanded = expandedDrawId === d.id
                  const formMode: FormMode = d.status === 'resulted' ? 'edit' : 'enter'
                  const showForm = expanded && (d.status === 'closed' || d.status === 'resulted')
                  return (
                    <Fragment key={d.id}>
                      <TableRow>
                        {/* The round name sits under the number rather than in its own
                            column: on the eleven lotteries that announce once a day it
                            would be a column of dashes. */}
                        <TableCell className="font-mono text-sm font-semibold">
                          #{d.drawNumber}
                          {d.roundLabel && (
                            <span className="block font-normal text-[10px] text-muted-foreground">
                              {d.roundLabel}
                            </span>
                          )}
                        </TableCell>
                        {/* The announcement moment, not just its calendar day — two draws
                            share a date whenever a lottery announces more than once. */}
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          <time dateTime={d.drawAt}>
                            {format(new Date(d.drawAt), 'dd/MM HH:mm')}
                          </time>
                        </TableCell>
                        <TableCell>
                          <Badge variant={meta.variant} className="text-[10px]">
                            {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {d.result ? (
                            <span className="inline-flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="font-semibold tracking-widest text-foreground">
                                  {d.result.prizeFirst}
                                </span>
                                {d.result.bottomTwo !== null && (
                                  <>
                                    <span className="text-muted-foreground">·</span>
                                    <span className="font-semibold tracking-widest text-foreground">
                                      {d.result.bottomTwo}
                                    </span>
                                  </>
                                )}
                              </span>
                              {/* The two 3-digit prizes of the Thai government lottery sit on a
                                  second line: on one line they widen the table past its card. */}
                              {(d.result.frontThree || d.result.bottomThree) && (
                                <span className="text-[10px] text-muted-foreground">
                                  {d.result.frontThree && <>หน้า {d.result.frontThree.join(' ')}</>}
                                  {d.result.frontThree && d.result.bottomThree && ' · '}
                                  {d.result.bottomThree && <>ท้าย {d.result.bottomThree.join(' ')}</>}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          <time dateTime={d.closeAt}>
                            {format(new Date(d.closeAt), 'dd/MM HH:mm')}
                          </time>
                        </TableCell>
                        <TableCell className="text-right">
                          {/* Each action is drawn only for an admin who holds it, so a
                              limited admin sees the ones they can actually complete
                              rather than a row of buttons that 403. */}
                          <div className="flex items-center justify-end gap-2">
                            {/* Betting must be stopped before a result can be entered —
                                one action at a time, in order. (The API still auto-closes
                                as a safety net if a result arrives on an open draw.) */}
                            {(d.status === 'open' || d.status === 'scheduled') && can('draws:open') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5"
                                onClick={() => setCloseTarget(d)}
                              >
                                <Lock className="h-3 w-3" />
                                {t.draws.closeBetting}
                              </Button>
                            )}

                            {/* Undo an early close — only while no result has been entered
                                yet. Once a result exists the draw has already moved to
                                'resulted', which this condition does not match. */}
                            {d.status === 'closed' && can('draws:open') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5"
                                onClick={() => void handleReopen(d)}
                                disabled={reopenBusyId === d.id}
                              >
                                {reopenBusyId === d.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <LockOpen className="h-3 w-3" />
                                )}
                                {reopenBusyId === d.id ? t.draws.reopening : t.draws.reopenBetting}
                              </Button>
                            )}

                            {/* 'resulted' with no result row is only reachable if the
                                status was written outside the service — offer the entry
                                form so it can be repaired rather than a settle that fails. */}
                            {(d.status === 'closed' || (d.status === 'resulted' && d.result === null))
                              && can('draws:result') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5"
                                onClick={() => setExpandedDrawId(expanded ? null : d.id)}
                              >
                                {expanded ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : d.status === 'closed' ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <AlertTriangle className="h-3 w-3 text-warning" />
                                )}
                                {expanded ? t.common.close : t.draws.enterResult}
                              </Button>
                            )}

                            {d.status === 'resulted' && d.result !== null && can('draws:result') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5"
                                onClick={() => setExpandedDrawId(expanded ? null : d.id)}
                              >
                                {expanded ? <ChevronUp className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                                {expanded ? t.common.close : t.draws.editResult}
                              </Button>
                            )}

                            {d.status === 'resulted' && d.result !== null && can('draws:settle') && (
                              <Button
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5"
                                onClick={() => setSettleTarget(d)}
                              >
                                <Coins className="h-3 w-3" />
                                {t.draws.settle}
                              </Button>
                            )}

                            {d.status === 'settled' && can('draws:void') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5 text-destructive hover:text-destructive"
                                onClick={() => setVoidTarget(d)}
                              >
                                <Undo2 className="h-3 w-3" />
                                {t.draws.voidAction}
                              </Button>
                            )}

                            {/* Available until money has moved on a result: once a draw is
                                settled the way back is void, which reverses payouts first. */}
                            {/* งดออกผล — same money movement as a cancel, but the งวด stays
                                on the member's list reading "งดออกผล" and keeps its slot, so
                                the scheduler will not re-open the round it just skipped. */}
                            {d.status !== 'settled' && d.status !== 'cancelled' && d.status !== 'skipped' && can('draws:cancel') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5 text-warning hover:text-warning"
                                onClick={() => setSkipTarget(d)}
                              >
                                <CalendarOff className="h-3 w-3" />
                                {t.draws.skipDraw}
                              </Button>
                            )}

                            {d.status !== 'settled' && d.status !== 'cancelled' && d.status !== 'skipped' && can('draws:cancel') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 font-mono text-xs gap-1.5 text-destructive hover:text-destructive"
                                onClick={() => setCancelTarget(d)}
                              >
                                <Ban className="h-3 w-3" />
                                {t.draws.cancelDraw}
                              </Button>
                            )}

                            {d.status === 'cancelled' && (
                              <span className="font-mono text-xs text-muted-foreground">
                                {t.draws.statusCancelled}
                              </span>
                            )}

                            {d.status === 'skipped' && (
                              <span className="font-mono text-xs text-warning">
                                {t.draws.statusSkipped}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {showForm && selectedLottery && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={6} className="p-0">
                            <ResultForm
                              lottery={selectedLottery}
                              draw={d}
                              mode={formMode}
                              onDone={handleFormDone}
                              onCancel={() => setExpandedDrawId(null)}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {settleTarget && selectedLottery && (
        <SettleConfirmDialog
          lottery={selectedLottery}
          draw={settleTarget}
          onSettled={handleSettled}
          onClose={() => setSettleTarget(null)}
        />
      )}

      {voidTarget && selectedLottery && (
        <VoidConfirmDialog
          lottery={selectedLottery}
          draw={voidTarget}
          onProceed={() => { setOtpVoid(voidTarget); setVoidTarget(null) }}
          onClose={() => setVoidTarget(null)}
        />
      )}

      {cancelTarget && selectedLottery && (
        <CancelDrawDialog
          lottery={selectedLottery}
          draw={cancelTarget}
          onProceed={(reason) => {
            setOtpRefund({ draw: cancelTarget, mode: 'cancel', reason })
            setCancelTarget(null)
          }}
          onClose={() => setCancelTarget(null)}
        />
      )}

      {skipTarget && selectedLottery && (
        <CancelDrawDialog
          lottery={selectedLottery}
          draw={skipTarget}
          mode="skip"
          onProceed={(reason) => {
            setOtpRefund({ draw: skipTarget, mode: 'skip', reason })
            setSkipTarget(null)
          }}
          onClose={() => setSkipTarget(null)}
        />
      )}

      {otpVoid && (
        <OtpConfirmDialog
          purpose="draw.settlement.void"
          payload={{ params: { drawId: otpVoid.id } }}
          title={t.draws.voidConfirmTitle}
          description={`${selectedLottery?.name ?? ''} ${t.draws.drawNumber} ${otpVoid.drawNumber} — ` +
            'ดึงยอดรางวัลที่จ่ายไปแล้วคืน และปลดงวดกลับไปสถานะรอจ่าย'}
          confirmLabel={t.draws.voidConfirmSubmit}
          danger
          onConfirm={async (otp) => { handleVoided(await apiDrawVoid(otpVoid.id, otp)) }}
          onDone={() => setOtpVoid(null)}
          onClose={() => setOtpVoid(null)}
        />
      )}

      {otpRefund && (
        <OtpConfirmDialog
          purpose={otpRefund.mode === 'skip' ? 'draw.skip' : 'draw.cancel'}
          payload={{ params: { drawId: otpRefund.draw.id }, body: { reason: otpRefund.reason } }}
          title={otpRefund.mode === 'skip' ? t.draws.skipTitle : t.draws.cancelTitle}
          description={`${selectedLottery?.name ?? ''} ${t.draws.drawNumber} ${otpRefund.draw.drawNumber} — ` +
            `คืนเงินเดิมพันทุกรายการในงวดนี้ · เหตุผล: ${otpRefund.reason}`}
          confirmLabel={otpRefund.mode === 'skip' ? t.draws.skipConfirm : t.draws.cancelConfirm}
          danger
          onConfirm={async (otp) => {
            const call = otpRefund.mode === 'skip' ? apiDrawSkip : apiDrawCancel
            const summary = await call(otpRefund.draw.id, otpRefund.reason, otp)
            if (otpRefund.mode === 'skip') handleSkipped(summary)
            else handleCancelled(summary)
          }}
          onDone={() => setOtpRefund(null)}
          onClose={() => setOtpRefund(null)}
        />
      )}

      {rulesOpen && selectedLottery && (
        <RulesDialog
          lottery={selectedLottery}
          onSaved={() => {
            setRulesOpen(false)
            setSuccessMsg(t.draws.rulesSaved)
          }}
          onCancel={() => setRulesOpen(false)}
        />
      )}

      {settingsOpen && selectedLottery && (
        <SettingsDialog
          lottery={selectedLottery}
          onSaved={() => {
            setSettingsOpen(false)
            setSuccessMsg(t.draws.settingsSaved)
            void loadLotteries()
          }}
          onCancel={() => setSettingsOpen(false)}
        />
      )}

      {createOpen && selectedLottery && (
        <CreateDrawDialog
          lottery={selectedLottery}
          after={createAfter}
          onCreated={handleCreated}
          onCancel={closeCreate}
        />
      )}

      {addOpen && (
        <AddLotteryDialog
          onCancel={() => setAddOpen(false)}
          onCreated={(name) => {
            setAddOpen(false)
            setSuccessMsg(t.draws.addLotteryDone(name))
            void loadLotteries()
          }}
        />
      )}

      {closeTarget && selectedLottery && (
        <CloseConfirmDialog
          lottery={selectedLottery}
          draw={closeTarget}
          onClosed={handleClosed}
          onCancel={() => setCloseTarget(null)}
        />
      )}
    </div>
  )
}
